import apiClient from './apiClient';
import { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

export interface SubscribeOnBehalfRequest {
  basket_id: string;
  zebu_client_id: string;
  lot_multiplier: number;
}

export interface SubscribeOnBehalfResponse {
  subscription_id: string;
  allocation_id: string;
  managed_user_id: string;
  basket_id: string;
  lot_multiplier: number;
  status: string;
}

export interface ManagedSubscription {
  subscription_id: string;
  user_id: string;
  marketplace_basket_id: string;
  admin_user_id: string;
  acting_admin_user_id?: string;
  zebu_client_id: string;
  lot_multiplier: number;
  allocation_id: string;
  status: string;
  subscribed_at: string;
  updated_at?: string;
}

class ManagedMarketplaceService {
  /** Subscribe a managed user to a marketplace listing on behalf of the
   *  admin. Allocation + subscription rows land under the managed user_id. */
  async subscribeOnBehalf(
    managedUserId: string,
    request: SubscribeOnBehalfRequest,
  ): Promise<SubscribeOnBehalfResponse> {
    try {
      const response = await apiClient.post<ApiResponse<SubscribeOnBehalfResponse>>(
        `/admin/managed-users/${managedUserId}/marketplace/subscribe`,
        request,
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to subscribe managed user');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to subscribe managed user',
      );
    }
  }

  /** List a managed user's marketplace subscriptions (admin view). */
  async listManagedSubscriptions(managedUserId: string): Promise<ManagedSubscription[]> {
    try {
      const response = await apiClient.get<ApiResponse<ManagedSubscription[]>>(
        `/admin/managed-users/${managedUserId}/marketplace/subscriptions`,
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to list managed subscriptions');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to list managed subscriptions',
      );
    }
  }

  /** Cancel a managed user's subscription on the admin's behalf. */
  async unsubscribeOnBehalf(managedUserId: string, subscriptionId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse>(
        `/admin/managed-users/${managedUserId}/marketplace/subscriptions/${subscriptionId}`,
        undefined,
        API_TYPE,
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to unsubscribe managed user');
      }
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to unsubscribe managed user',
      );
    }
  }
}

const managedMarketplaceService = new ManagedMarketplaceService();
export default managedMarketplaceService;
